import { useEffect, useRef, useState } from 'react';

// Las barras del navegador que se contraen al desplazar mueven el alto ~60-100px
// y NO son un teclado. Por debajo de este umbral no se decide nada.
const THRESHOLD_PX = 160;

/**
 * `true` mientras el teclado virtual del móvil está abierto.
 *
 * Sirve para esconder barras fijas inferiores, que si no quedan flotando justo
 * encima del teclado y roban la mitad del hueco que queda para escribir.
 *
 * Se miran DOS señales porque los sistemas se comportan distinto: en iOS el
 * teclado tapa la ventana (lo ve visualViewport) y en Android con
 * `interactive-widget=resizes-content` la encoge entera (lo ve la caída del
 * alto respecto al máximo observado).
 */
export function useVirtualKeyboard(): boolean {
  const [isOpen, setIsOpen] = useState(false);
  const baselineRef = useRef(0);

  useEffect(() => {
    // Solo en dispositivos táctiles: en escritorio, encoger la ventana no es un
    // teclado y no debe esconder nada.
    if (!window.matchMedia?.('(pointer: coarse)').matches) return;

    const viewport = window.visualViewport;

    const update = () => {
      const innerHeight = window.innerHeight;
      if (innerHeight > baselineRef.current) baselineRef.current = innerHeight;

      // iOS: la ventana no cambia, el teclado tapa parte del viewport visual.
      const occluded = viewport
        ? innerHeight - viewport.height - viewport.offsetTop
        : 0;
      // Android (resizes-content): la ventana entera encoge.
      const shrunk = baselineRef.current - innerHeight;

      setIsOpen(occluded > THRESHOLD_PX || shrunk > THRESHOLD_PX);
    };

    const resetBaseline = () => {
      // Al girar el dispositivo el alto de referencia es otro; conservar el
      // anterior dejaría la barra escondida para siempre en horizontal.
      baselineRef.current = 0;
      update();
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', resetBaseline);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', resetBaseline);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
    };
  }, []);

  return isOpen;
}
